/**
 * Auftragszeit erfassen — `PWA-004`.
 *
 * **Auftragszeit, nicht Arbeitszeit.** `01` §1.12 trennt beides scharf, und
 * ADR-031 hält fest, dass die Plattform die gesetzliche Aufzeichnungspflicht
 * ausdrücklich nicht abdeckt. Der Unterschied steht auch auf dem Bildschirm
 * und nicht nur im Dokument: Diese Ansicht ist der Ort, an dem ein Monteur
 * sonst annähme, er habe damit seinen Arbeitstag erfasst.
 *
 * **Die Zeiten sind abrechnungsfähig** (`CLAUDE.md`, `07` §7.8) — deshalb hat
 * jeder Posten eine Art und einen Schalter dafür. Eine freie Notiz „ca. 3
 * Stunden" wäre nicht übergabefähig, und die Übergabe an die Branchensoftware
 * gehört nach `EXP-001` zum Produkt.
 *
 * **Die Anfahrt ist eine eigene Position.** Das ist der Grund, aus dem ADR-045
 * den Rücksprung `uebermittelt → vor-ort` verbietet: Bei zwei Vor-Ort-Blöcken
 * mit Lücke wäre nicht mehr entscheidbar, welche Anfahrt zu welcher Arbeit
 * gehört.
 */

import { useState } from "react";

import { dauerMinuten, summeMinuten, zeitenZuEinsatz } from "@hwc/demo-data";
import type { Einsatz } from "@hwc/demo-data";
import { ZEITART } from "@hwc/demo-data";
import type { Zeitart } from "@hwc/demo-data";

import { useMonteur } from "../../src/Sitzung.js";
import { alsZeitpunkt, dauer, jetztHHMM, uhrzeit } from "../../src/bausteine.js";

const ARTTEXT: Record<Zeitart, string> = {
  anfahrt: "Anfahrt",
  arbeit: "Arbeit",
  ruestzeit: "Rüstzeit",
  rueckfahrt: "Rückfahrt",
};

export function Zeiterfassung({ einsatz }: { einsatz: Einsatz }) {
  const { bestand, auflage, zustandVon, buchZeit } = useMonteur();

  const gesendet = zeitenZuEinsatz(bestand, einsatz.ref);
  const eigene = auflage(einsatz).zeiten;
  const gesperrt = zustandVon(einsatz) === "geprueft" || zustandVon(einsatz) === "storniert";

  const [offen, setzeOffen] = useState(false);
  const [art, setzeArt] = useState<Zeitart>("arbeit");
  const [von, setzeVon] = useState(jetztHHMM());
  const [bis, setzeBis] = useState(jetztHHMM());
  const [abrechenbar, setzeAbrechenbar] = useState(true);
  const [notiz, setzeNotiz] = useState("");
  const [fehler, setzeFehler] = useState<string | null>(null);

  const summeGesendet = summeMinuten(gesendet);
  const summeEigen = eigene.reduce((s, z) => s + dauerMinuten(z.von, z.bis), 0);

  function speichern() {
    const vonZeit = alsZeitpunkt(von);
    const bisZeit = alsZeitpunkt(bis);
    const minuten = dauerMinuten(vonZeit, bisZeit);

    // Zwei Prüfungen, und beide fangen einen Tippfehler ab, den man auf einem
    // Handydisplay leicht macht. Sie melden **vor** dem Speichern — eine
    // Buchung mit negativer Dauer, die erst das Büro auffällt, kostet dort
    // einen Anruf.
    if (minuten <= 0) {
      setzeFehler("Das Ende liegt vor dem Beginn. Bitte die Zeiten prüfen.");
      return;
    }
    if (minuten > 720) {
      setzeFehler(`${dauer(minuten)} auf einer Position — ist das richtig?`);
      return;
    }

    buchZeit(einsatz, {
      art,
      von: vonZeit,
      bis: bisZeit,
      abrechenbar,
      ...(notiz.trim() ? { notiz: notiz.trim() } : {}),
    });
    setzeFehler(null);
    setzeNotiz("");
    setzeOffen(false);
  }

  return (
    <>
      <h2>Zeit</h2>

      {gesendet.length === 0 && eigene.length === 0 ? (
        <p className="leer leise">Noch keine Zeit gebucht.</p>
      ) : (
        <ul className="postenliste">
          {gesendet.map((z) => (
            <li key={z.ref.opaqueId}>
              <span>
                {ARTTEXT[z.art]} · {uhrzeit(z.von)}–{uhrzeit(z.bis)}
                {!z.abrechenbar && (
                  <span className="abzeichen abzeichen--neutral">nicht abrechenbar</span>
                )}
                {z.notiz && <span className="leise"> — {z.notiz}</span>}
              </span>
              <span>{dauer(dauerMinuten(z.von, z.bis))}</span>
            </li>
          ))}
          {eigene.map((z) => (
            <li key={z.id} className="postenliste__wartend">
              <span>
                {ARTTEXT[z.art as Zeitart] ?? z.art} · {uhrzeit(z.von)}–{uhrzeit(z.bis)}
                <span className="abzeichen abzeichen--warnung">wartet auf Upload</span>
                {!z.abrechenbar && (
                  <span className="abzeichen abzeichen--neutral">nicht abrechenbar</span>
                )}
                {z.notiz && <span className="leise"> — {z.notiz}</span>}
              </span>
              <span>{dauer(dauerMinuten(z.von, z.bis))}</span>
            </li>
          ))}
        </ul>
      )}

      {(summeGesendet > 0 || summeEigen > 0) && (
        <p className="summe">
          Gesamt {dauer(summeGesendet + summeEigen)}
          {summeEigen > 0 && (
            <span className="leise"> · davon {dauer(summeEigen)} noch nicht gesendet</span>
          )}
        </p>
      )}

      {!gesperrt
        && (offen ? (
          <div className="formular">
            <label>
              Art
              <select value={art} onChange={(e) => setzeArt(e.target.value as Zeitart)}>
                {ZEITART.map((a) => (
                  <option key={a} value={a}>
                    {ARTTEXT[a]}
                  </option>
                ))}
              </select>
            </label>
            <div className="formular__paar">
              <label>
                Von
                <input type="time" value={von} onChange={(e) => setzeVon(e.target.value)} />
              </label>
              <label>
                Bis
                <input type="time" value={bis} onChange={(e) => setzeBis(e.target.value)} />
              </label>
            </div>
            <label className="schalter">
              <input
                type="checkbox"
                checked={abrechenbar}
                onChange={(e) => setzeAbrechenbar(e.target.checked)}
              />
              Abrechenbar
            </label>
            <label>
              Notiz (freiwillig)
              <input
                type="text"
                value={notiz}
                onChange={(e) => setzeNotiz(e.target.value)}
                placeholder="z. B. Zuschlag außerhalb der Regelarbeitszeit"
              />
            </label>

            {fehler && <p className="marke marke--notfall">{fehler}</p>}

            <div className="knopfleiste">
              <button
                type="button"
                className="knopfleiste__knopf knopfleiste__knopf--haupt"
                onClick={speichern}
              >
                Zeit buchen
              </button>
              <button
                type="button"
                className="knopfleiste__knopf"
                onClick={() => {
                  setzeOffen(false);
                  setzeFehler(null);
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <div className="knopfleiste">
            <button
              type="button"
              className="knopfleiste__knopf"
              onClick={() => {
                setzeVon(jetztHHMM());
                setzeBis(jetztHHMM());
                setzeOffen(true);
              }}
            >
              Zeit buchen
            </button>
          </div>
        ))}

      <p className="leise fussnote">
        <strong>Auftragszeit, nicht Arbeitszeit.</strong> Diese Buchungen gehören zum
        Einsatz und sind Grundlage der Abrechnung. Die gesetzliche Arbeitszeiterfassung —
        Beginn, Pause, Ende Ihres Arbeitstags — leistet die App nach ADR-031 nicht.
      </p>
    </>
  );
}
